/**
 * A result as typed by a technician, parsed for flagging.
 * Handles Indian digit grouping ("1,30,000"), censored values ("<0.5", ">1000")
 * and counts reported as a span ("2-4" pus cells per hpf).
 */
export type ParsedValue =
  | { kind: 'empty' }
  | { kind: 'number'; value: number }
  | { kind: 'censored'; value: number; comparator: '<' | '<=' | '>' | '>=' }
  | { kind: 'span'; low: number; high: number }
  | { kind: 'text'; text: string };

const NUMBER = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

function toNumber(s: string): number | null {
  const cleaned = s.replace(/,/g, '').trim();
  return NUMBER.test(cleaned) ? Number(cleaned) : null;
}

export function parseValue(raw: string | null | undefined): ParsedValue {
  const text = (raw ?? '').trim();
  if (text === '') return { kind: 'empty' };

  const plain = toNumber(text);
  if (plain !== null) return { kind: 'number', value: plain };

  const censored = /^(<=|>=|<|>|≤|≥)\s*(.+)$/.exec(text);
  if (censored) {
    const n = toNumber(censored[2] ?? '');
    if (n !== null) {
      const op = censored[1] === '≤' ? '<=' : censored[1] === '≥' ? '>=' : censored[1];
      return { kind: 'censored', value: n, comparator: op as '<' | '<=' | '>' | '>=' };
    }
  }

  const span = /^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)$/.exec(text);
  if (span) {
    const low = Number(span[1]);
    const high = Number(span[2]);
    if (low <= high) return { kind: 'span', low, high };
  }

  return { kind: 'text', text };
}

/** The value used for arithmetic in formulas; spans and text have none. */
export function numericOf(v: ParsedValue): number | null {
  if (v.kind === 'number' || v.kind === 'censored') return v.value;
  return null;
}

/** Words labs use interchangeably for a normal negative finding. */
const NEGATIVE_WORDS = new Set([
  'nil',
  'negative',
  'neg',
  'absent',
  'not detected',
  'non reactive',
  'nonreactive',
  'non-reactive',
  'none seen',
  'not seen',
]);

export function normalizeQualitative(s: string): string {
  const t = s.trim().toLowerCase().replace(/\s+/g, ' ');
  return NEGATIVE_WORDS.has(t) ? 'negative' : t;
}
